import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export function openChannelMessenger(): void {
  window.ChannelIO?.("showMessenger");
}

interface ChannelTalkContextValue {
  isMessengerOpen: boolean;
  openMessenger: () => void;
  isMobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

const ChannelTalkContext = createContext<ChannelTalkContextValue | null>(null);

export function ChannelTalkProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [isMessengerOpen, setIsMessengerOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const registerMessengerCallbacks = () => {
    if (!window.ChannelIO) return false;

    window.ChannelIO("onShowMessenger", () => {
      setIsMessengerOpen(true);
    });
    window.ChannelIO("onHideMessenger", () => {
      setIsMessengerOpen(false);
    });

    return true;
  };

  useEffect(() => {
    let intervalId: number | undefined;

    if (!registerMessengerCallbacks()) {
      // Cap the polling: if ChannelIO never loads (e.g. the script is blocked), stop after
      // MAX_ATTEMPTS so the 100ms interval does not run for the whole Provider lifetime.
      let attempts = 0;
      const MAX_ATTEMPTS = 100; // ~10s
      intervalId = window.setInterval(() => {
        attempts += 1;
        if (
          (registerMessengerCallbacks() || attempts >= MAX_ATTEMPTS) &&
          intervalId !== undefined
        ) {
          window.clearInterval(intervalId);
        }
      }, 100);
    }

    return () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, []);

  const openMessenger = () => {
    registerMessengerCallbacks();
    openChannelMessenger();
    setIsMessengerOpen(true);
  };

  const setMobileMenuOpen = useCallback((open: boolean) => {
    setIsMobileMenuOpen(open);
  }, []);

  const value = useMemo(
    () => ({
      isMessengerOpen,
      openMessenger,
      isMobileMenuOpen,
      setMobileMenuOpen,
    }),
    [isMessengerOpen, isMobileMenuOpen, setMobileMenuOpen]
  );

  return (
    <ChannelTalkContext.Provider value={value}>
      {children}
    </ChannelTalkContext.Provider>
  );
}

export function useChannelTalkMessengerOpen(): ChannelTalkContextValue {
  const context = useContext(ChannelTalkContext);
  if (!context) {
    throw new Error(
      "useChannelTalkMessengerOpen must be used within ChannelTalkProvider"
    );
  }
  return context;
}
